import { ErrorResponse, fetchAPI } from '@quickbit/backoffice-core-frontend';
import { toSnakeCase } from '@quickbit/backoffice-core-frontend/src/api/fetchAPI';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

export type CaseCustomerType = 'AFFILIATE' | 'APP' | 'MERCHANT';

export enum CaseState {
  ACTIVE = 'ACTIVE',
  CLOSED = 'CLOSED',
}

export interface CaseNote {
  createdAt: string;
  content: string;
  createdBy: string;
}

export type ScreeningMatchStatus =
  | 'NOT_INVESTIGATED'
  | 'PEP_MATCH'
  | 'RCA_MATCH'
  | 'SIP_MATCH'
  | 'OOL_MATCH'
  | 'SANCTION_MATCH'
  | 'FALSE_POSITIVE';

export interface ScreeningMatch {
  uuid: string;
  status: ScreeningMatchStatus;
  matchData: Record<string, unknown>;
  dowJonesProfileId?: string;
  comment: string;
  createdAt: string;
}

export interface CaseItem {
  uuid: string;
  customerType: CaseCustomerType;
  customerId: string;
  transactionId: string | null;
  title: string;
  additionalInformation: Record<string, unknown>;
  state: CaseState;
  createdAt: string;
  notes: CaseNote[];
  screeningMatches: ScreeningMatch[];
  screeningMatchesTotal?: number;
  wakeupAt: string | null;
  reportedToFipo: boolean;
}

export interface TitleCount {
  title: string;
  count: number;
}

export interface CasesSummary {
  total: number;
  perTitle: TitleCount[];
}

export interface CasesPagination {
  total: number;
  page: number;
  size: number;
}

export interface CasesResponse {
  cases: CaseItem[];
  summary: CasesSummary;
  pagination: CasesPagination;
}

export interface CaseDetailsResponse {
  case: CaseItem;
}

const queryKeys = {
  all: () => ['fcu', 'cases'] as const,
  lists: () => [...queryKeys.all(), 'list'] as const,
  list: (query: string) => [...queryKeys.lists(), query] as const,
  details: (caseId: string) => [...queryKeys.all(), 'details', caseId] as const,
  detail: (
    caseId: string,
    screeningMatchesPage: number,
    screeningMatchesPageSize: number,
    screeningMatchesSearch: string,
    screeningMatchesStatus: ScreeningMatchStatus | undefined,
    includePausedMedia: boolean
  ) =>
    [
      ...queryKeys.details(caseId),
      screeningMatchesPage,
      screeningMatchesPageSize,
      screeningMatchesSearch,
      screeningMatchesStatus ?? '',
      includePausedMedia,
    ] as const,
};

export const useGetCases = (query: Record<string, any>) => {
  const queryString = new URLSearchParams(
    Object.fromEntries(
      Object.entries(toSnakeCase(query))
        .filter(([, value]) => value !== undefined)
        .map(([key, value]) => [key, String(value)])
    )
  ).toString();
  return useQuery<CasesResponse, ErrorResponse>({
    queryKey: queryKeys.list(queryString),
    queryFn: async () => {
      const response = await fetchAPI<CasesResponse>(
        `v1/fcu/cases${queryString ? `?${queryString}` : ''}`
      );
      return response;
    },
    placeholderData: keepPreviousData,
  });
};

export const useGetCaseDetails = (
  caseId: string,
  screeningMatchesPage = 1,
  screeningMatchesPageSize = 100,
  screeningMatchesSearch = '',
  screeningMatchesStatus?: ScreeningMatchStatus,
  includePausedMedia = false
) => {
  return useQuery<CaseDetailsResponse, ErrorResponse>({
    queryKey: queryKeys.detail(
      caseId,
      screeningMatchesPage,
      screeningMatchesPageSize,
      screeningMatchesSearch,
      screeningMatchesStatus,
      includePausedMedia
    ),
    queryFn: async () => {
      const query = new URLSearchParams({
        screening_matches_page: String(screeningMatchesPage),
        screening_matches_page_size: String(screeningMatchesPageSize),
        include_paused_media: String(includePausedMedia),
      });
      if (screeningMatchesSearch) {
        query.set('screening_matches_search', screeningMatchesSearch);
      }
      if (screeningMatchesStatus) {
        query.set('screening_matches_status', screeningMatchesStatus);
      }
      return await fetchAPI<CaseDetailsResponse>(
        `v1/fcu/cases/${caseId}?${query.toString()}`
      );
    },
    enabled: !!caseId,
    placeholderData: keepPreviousData,
  });
};

export const useAddCaseNote = (caseId: string) => {
  const queryClient = useQueryClient();
  return useMutation<{ uuid: string }, ErrorResponse, { content: string }>({
    mutationFn: async ({ content }) => {
      return await fetchAPI<{ uuid: string }>(`v1/fcu/cases/${caseId}/notes`, {
        method: 'POST',
        body: { content },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.details(caseId),
      });
    },
  });
};

export const useUpdateCase = (caseId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean },
    ErrorResponse,
    { state: CaseState; reportedToFipo: boolean }
  >({
    mutationFn: async ({ state, reportedToFipo }) => {
      return await fetchAPI<{ success: boolean }>(`v1/fcu/cases/${caseId}`, {
        method: 'PATCH',
        body: { state, reportedToFipo },
      });
    },
    onSuccess: (_response, { state, reportedToFipo }) => {
      queryClient.setQueriesData<CaseDetailsResponse>(
        { queryKey: queryKeys.details(caseId) },
        (current) =>
          current
            ? {
                case: {
                  ...current.case,
                  state,
                  reportedToFipo,
                },
              }
            : current
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.details(caseId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.lists(),
      });
    },
  });
};

export const usePostponeCase = (caseId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean },
    ErrorResponse,
    { postponedUntil: Date; note: string }
  >({
    mutationFn: async ({ postponedUntil, note }) => {
      return await fetchAPI<{ success: boolean }>(`v1/fcu/cases/${caseId}`, {
        method: 'PUT',
        body: { wakeupAt: postponedUntil.toISOString(), note },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.lists() });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.details(caseId),
      });
    },
  });
};

export interface CreateCaseInput {
  customerId: string;
  customerType: CaseCustomerType;
  title: string;
  wakeupAt?: Date;
  note: string;
}

export const useCreateCase = () => {
  const queryClient = useQueryClient();
  return useMutation<{ uuid: string }, ErrorResponse, CreateCaseInput>({
    mutationFn: async ({ customerId, customerType, title, wakeupAt, note }) => {
      return await fetchAPI<{ uuid: string }>(`v1/fcu/cases`, {
        method: 'POST',
        body: {
          customerId,
          customerType,
          title,
          wakeupAt: wakeupAt?.toISOString(),
          note,
        },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.lists() });
    },
  });
};

export const useDeleteCase = (caseId: string) => {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean }, ErrorResponse>({
    mutationFn: async () => {
      return await fetchAPI<{ success: boolean }>(`v1/fcu/cases/${caseId}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.lists() });
    },
  });
};

export const useUpdateScreeningMatchStatus = (caseId: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    { success: boolean },
    ErrorResponse,
    { matchId: string; status?: ScreeningMatchStatus; comment?: string }
  >({
    mutationFn: async ({ matchId, status, comment }) => {
      return await fetchAPI<{ success: boolean }>(
        `v1/fcu/cases/${caseId}/screening-matches/${matchId}`,
        {
          method: 'PATCH',
          body: { status, comment },
        }
      );
    },
    onSuccess: (_response, { matchId, status, comment }) => {
      queryClient.setQueriesData<CaseDetailsResponse>(
        { queryKey: queryKeys.details(caseId) },
        (current) =>
          current
            ? {
                case: {
                  ...current.case,
                  screeningMatches: current.case.screeningMatches.map((match) =>
                    match.uuid === matchId
                      ? {
                          ...match,
                          status: status ?? match.status,
                          comment: comment ?? match.comment,
                        }
                      : match
                  ),
                },
              }
            : current
      );
      if (status) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.details(caseId),
        });
      }
    },
  });
};

export const useBatchUpdateCaseState = () => {
  const queryClient = useQueryClient();
  return useMutation<
    Record<string, boolean>,
    ErrorResponse,
    { caseIds: string[]; state: CaseState }
  >({
    mutationFn: async ({ caseIds, state }) => {
      const results = await Promise.all(
        caseIds.map(async (caseId) => {
          try {
            await fetchAPI<{ success: boolean }>(`v1/fcu/cases/${caseId}`, {
              method: 'PATCH',
              body: { state },
            });
            return { [caseId]: true };
          } catch {
            return { [caseId]: false };
          }
        })
      );
      return Object.assign({}, ...results) as Record<string, boolean>;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.lists() });
    },
  });
};

export const useBatchUpdateReportedToFipo = () => {
  const queryClient = useQueryClient();
  return useMutation<
    Record<string, boolean>,
    ErrorResponse,
    { caseIds: string[]; reportedToFipo: boolean }
  >({
    mutationFn: async ({ caseIds, reportedToFipo }) => {
      const results = await Promise.all(
        caseIds.map(async (caseId) => {
          try {
            await fetchAPI<{ success: boolean }>(`v1/fcu/cases/${caseId}`, {
              method: 'PATCH',
              body: { reportedToFipo },
            });
            return { [caseId]: true };
          } catch {
            return { [caseId]: false };
          }
        })
      );
      return Object.assign({}, ...results) as Record<string, boolean>;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.lists() });
    },
  });
};

export const useBatchDeleteCases = () => {
  const queryClient = useQueryClient();
  return useMutation<
    Record<string, boolean>,
    ErrorResponse,
    { caseIds: string[] }
  >({
    mutationFn: async ({ caseIds }) => {
      const results = await Promise.all(
        caseIds.map(async (caseId) => {
          try {
            await fetchAPI<{ success: boolean }>(`v1/fcu/cases/${caseId}`, {
              method: 'DELETE',
            });
            return { [caseId]: true };
          } catch {
            return { [caseId]: false };
          }
        })
      );
      return Object.assign({}, ...results) as Record<string, boolean>;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.lists() });
    },
  });
};
